import { motion } from 'framer-motion'
import { pressable } from './motion'

interface Props {
  children: React.ReactNode
  className?: string
  /** Adds the tap-scale and a pointer cursor. Use on anything that navigates. */
  isPressable?: boolean
  /** Lifts the card off the page. Reserve for the one thing that leads a screen. */
  elevated?: boolean
  onClick?: () => void
  as?: 'div' | 'section' | 'article'
}

/**
 * The card the whole app is built from.
 *
 * Replaces the ad-hoc `<Card className="shadow-sm">` / `rounded-xl border
 * border-divider bg-content1` pairs that had drifted into four different
 * treatments. White paper, a hairline in the gym's own hue, a soft shadow.
 *
 * Deliberately not HeroUI's `Card`: that one carries its own radius, shadow and
 * background, so every use started by overriding three of its props.
 */
export function SurfaceCard({
  children,
  className = '',
  isPressable = false,
  elevated = false,
  onClick,
  as = 'div',
}: Props) {
  const surface = `rounded-lg border border-[var(--gym-line)] bg-content1 ${
    elevated ? 'shadow-e2' : 'shadow-e1'
  } ${className}`

  // A pressable card is a real button, not a div with a click handler: it has
  // to be reachable by keyboard and announced as something you can activate.
  if (isPressable) {
    return (
      <motion.button
        type="button"
        onClick={onClick}
        {...pressable}
        className={`text-left ${surface}`}
      >
        {children}
      </motion.button>
    )
  }

  const Component = motion[as]
  return (
    <Component onClick={onClick} className={surface}>
      {children}
    </Component>
  )
}
